import React from 'react';
import { Briefcase, MapPin, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { usePortfolio } from '@/context/PortfolioContext';
import {
  SectionShell,
  SectionInner,
  SectionHeader,
  SectionLoading,
  SectionError,
} from '@/components/section/sectionLayout';

const Experience = () => {
  const { portfolioData, loading, error } = usePortfolio();

  if (loading) return <SectionLoading id="experience" variant="alt" />;
  if (error) return <SectionError id="experience" error={error} variant="alt" />;

  const experiences = portfolioData?.experience || [];

  return (
    <SectionShell id="experience" variant="alt">
      <SectionInner>
        <SectionHeader title="Expérience" highlight="professionnelle" />

        {experiences.length === 0 ? (
          <p className="text-center text-gray-600 dark:text-gray-400">Aucune expérience à afficher.</p>
        ) : (
          <div className="relative">
            <div className="absolute bottom-0 left-[11px] top-0 w-0.5 bg-gradient-to-b from-emerald-500 to-teal-500 md:left-1/2 md:-translate-x-1/2" />

            <div className="space-y-8 sm:space-y-10 md:space-y-12">
              {experiences.map((exp, index) => {
                const achievements = exp.achievements || [];
                const expKey = exp.id ?? exp._id ?? index;

                return (
                  <div
                    key={expKey}
                    className={`relative md:flex md:items-start md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row-reverse' : ''
                    }`}
                  >
                    <div className="absolute left-0 top-6 z-10 flex h-6 w-6 items-center justify-center rounded-full border-4 border-white bg-emerald-500 dark:border-[#0a0e1a] md:left-1/2 md:-translate-x-1/2" />

                    <div className="min-w-0 flex-1 pl-10 md:pl-0 md:pt-0">
                      <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e] motion-safe:transition-shadow motion-safe:hover:shadow-lg motion-safe:hover:shadow-emerald-500/10">
                        <CardContent className="p-4 sm:p-6">
                          <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
                            {exp.title}
                          </h3>
                          <div className="mb-3 flex items-center gap-2 font-semibold text-emerald-500">
                            <Briefcase className="h-4 w-4 shrink-0" />
                            <span className="break-words">{exp.company}</span>
                          </div>

                          <div className="mb-4 flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-400 sm:flex-row sm:flex-wrap sm:gap-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 shrink-0" />
                              <span>{exp.period}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 shrink-0" />
                              <span>{exp.location}</span>
                            </div>
                          </div>

                          {achievements.length > 0 && (
                            <ul className="space-y-2">
                              {achievements.map((achievement, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 sm:text-base"
                                >
                                  <span className="mt-0.5 shrink-0 text-emerald-400">✓</span>
                                  <span>{achievement}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    <div className="hidden min-w-0 flex-1 md:block" aria-hidden />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SectionInner>
    </SectionShell>
  );
};

export default Experience;
